import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Menu, LogOut, Settings, User, Mail, Trophy } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '@/contexts/AuthContext';
import NotificationDropdown from './notifications/NotificationDropdown';
import ProfileDropdown from './ProfileDropdown';
import EnhancedBackButton from '@/components/EnhancedBackButton';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation';
import BrandLogo from './BrandLogo';
import { useIsMobile } from '@/hooks/use-mobile';
import { ROUTES } from '@/constants/routes';
import { motion } from 'framer-motion';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';

interface HeaderProps {
  title?: string;
  showBack?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
  rightElement?: React.ReactNode;
  leftComponent?: React.ReactNode;
  showNotifications?: boolean;
  showBreadcrumbs?: boolean;
  backButtonVariant?: 'icon' | 'text' | 'full' | 'enhanced';
}

const Header = ({
  title,
  showBack = false,
  showBackButton = false,
  onBack,
  rightElement,
  leftComponent,
  showNotifications = true,
  showBreadcrumbs = false,
  backButtonVariant = 'icon'
}: HeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, profile, signOut } = useAuth();
  const isMobile = useIsMobile();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Mobile Menu Dropdown Component
  const MobileMenuDropdown = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-11 w-11">
          <Menu className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{profile?.full_name}</p>
            <p className="text-xs leading-none text-muted-foreground">
              @{profile?.username}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/leagues')}>
          <Trophy className="mr-2 h-4 w-4" />
          <span>My Leagues</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(ROUTES.CONTACT)}>
          <Mail className="mr-2 h-4 w-4" />
          <span>{t('header.contactUs')}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(ROUTES.SETTINGS)}>
          <Settings className="mr-2 h-4 w-4" />
          <span>{t('header.settings')}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t('header.logOut')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header
      className="bg-background/95 backdrop-blur-md border-b border-border px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] flex items-center justify-between sticky top-0 z-50 shadow-sm"
    >
      {/* Left section */}
      <div className="flex items-center gap-2 sm:gap-4">
        {isMobile && (showBack || showBackButton) ? (
          <EnhancedBackButton 
            variant="icon" 
            onBack={onBack} 
            className="text-foreground hover:bg-primary/10" 
          />
        ) : isMobile && leftComponent ? (
          leftComponent
        ) : isMobile ? (
          <BrandLogo />
        ) : null}

        {!isMobile && (showBack || showBackButton) && (
          <EnhancedBackButton 
            variant={backButtonVariant === 'enhanced' ? 'full' : backButtonVariant as 'icon' | 'text' | 'full'} 
            onBack={onBack} 
            className="text-foreground hover:bg-primary/10" 
          />
        )}
        
        {!isMobile && !showBack && !showBackButton && (
          showBreadcrumbs ? (
            <BreadcrumbNavigation maxItems={4} />
          ) : (leftComponent ?? <BrandLogo />)
        )}
      </div>

      <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
        {rightElement}
        
        {user && (
          <div className="flex-shrink-0">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(ROUTES.HELP)}
              className="relative h-9 w-9 sm:h-10 sm:w-10"
            >
              <HelpCircle className="h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" />
            </Button>
          </div>
        )}
        
        {showNotifications && user && (
          <div className="flex-shrink-0">
            <NotificationDropdown />
          </div>
        )}
        
        {user ? (
          <div>
            {isMobile ? <MobileMenuDropdown /> : <ProfileDropdown />}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              onClick={() => navigate(ROUTES.AUTH)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-sm rounded-xl px-4 py-2 h-10"
            >
              {t('header.signIn')}
            </Button>
          </motion.div>
        )}
      </div>
    </header>
  );
};

export default Header;
